import './style.scss'
import React, { useState } from 'react'
import { Text, tokens, UIProps } from 'src/componentv2/Base/ui'

type FieldChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
type FieldFocusEvent = React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>

export interface TextFieldProps extends UIProps {
    label?: React.ReactNode
    placeholder?: string
    helperText?: React.ReactNode
    value?: string
    onChange?: (value: string, e: FieldChangeEvent) => void
    onFocus?: (e: FieldFocusEvent) => void
    onBlur?: (e: FieldFocusEvent) => void
    name?: string
    type?: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    error?: React.ReactNode
    disabled?: boolean
    required?: boolean
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>
    /** Renders a <textarea> instead of an <input> — prefix/suffix are ignored in this mode. */
    multiline?: boolean
    /** Only used when multiline is true. Defaults to 4. */
    rows?: number
    /** Only used when multiline is true — passed through to the underlying <textarea>. */
    textareaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>
}

export const TextField: React.FC<TextFieldProps> = ({
                                                        label, placeholder, helperText, value, onChange, onFocus, onBlur, name,
                                                        type = 'text', prefix, suffix, error, disabled, required,
                                                        width = '100%', className = '', inputProps,
                                                        multiline = false, rows = 4, textareaProps,
                                                    }) => {
    const [focused, setFocused] = useState(false)

    return (
        <div
            className={`field ${multiline ? 'field--multiline' : ''} ${error ? 'field--error' : ''} ${focused ? 'field--focused' : ''} ${className}`}
            style={{ width }}
        >
            {label && (
                <label className="field__label">
                    {label}{required && <span className="field__req"> *</span>}
                </label>
            )}

            <div className="field__control">
                {!multiline && prefix && <span className="field__prefix">{prefix}</span>}
                {multiline ? (
                    <textarea
                        className="field__input"
                        name={name}
                        value={value}
                        placeholder={placeholder}
                        disabled={disabled}
                        rows={rows}
                        onChange={(e) => onChange?.(e.target.value, e)}
                        onFocus={(e) => {
                            setFocused(true)
                            onFocus?.(e)
                        }}
                        onBlur={(e) => {
                            setFocused(false)
                            onBlur?.(e)
                        }}
                        {...textareaProps}
                    />
                ) : (
                    <input
                        className="field__input"
                        type={type}
                        name={name}
                        value={value}
                        placeholder={placeholder}
                        disabled={disabled}
                        onChange={(e) => onChange?.(e.target.value, e)}
                        onFocus={(e) => {
                            setFocused(true)
                            onFocus?.(e)
                        }}
                        onBlur={(e) => {
                            setFocused(false)
                            onBlur?.(e)
                        }}
                        {...inputProps}
                    />
                )}
                {!multiline && suffix && <span className="field__suffix">{suffix}</span>}
            </div>

            {(error || helperText) && (
                <Text
                    className="field__helper"
                    fontSize="1.3"
                    topMargin="0.6"
                    color={error ? '#D92D20' : tokens.textMuted}
                >
                    {error || helperText}
                </Text>
            )}
        </div>
    )
}

export default TextField